/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { JSX } from "react";
import { Edit, Delete } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, IconButton, Typography } from "@mui/material";

import {
  approveLeaveApplication,
  deleteLeaveApplication,
  rejectLeaveApplication
} from "../../../api/leaveApplication";
import { RootState } from "../../../store";
import Panel from "../../../components/Panel";
import { t, tl } from "../../../components/translate";
import EmployeeInfoPanel from "../EmployeInfoPanel";
import OkhatiDialog from "../../../components/Dialog/Dialog";
import useIsUserRcAdmin from "../../../hooks/useIsUserRcAdmin";
import { notificationAdd } from "../../../actions/notification";
import { ILeaveApplication } from "../../../interfaces/LeaveApplicationInterface";
import { bsFullDate } from "../../../components/Calendar/functions/calendarFunctions";
import { LeaveApplicationStatus } from "../../../constants/leave";
import { LeaveApplicationStatusChip } from "./LeaveApplicationStatusChip";

interface LeaveApplicationInfoProps {
  leaveApplication: ILeaveApplication;
  onClose: () => void;
  onEdit: (application: ILeaveApplication) => void;
  setLeaveApplications: React.Dispatch<React.SetStateAction<ILeaveApplication[]>>;
  setSelectedLeaveApplication: React.Dispatch<React.SetStateAction<ILeaveApplication | null>>;
}

function DetailRow({
  label,
  value,
  sx,
  onClick
}: {
  label: string;
  value: any;
  sx?: any;
  onClick?: () => void;
}) {
  return (
    <Box sx={{ display: "flex" }}>
      <Typography variant="body2" fontWeight={600} width="150px">
        {label}
      </Typography>
      <Typography variant="body2" sx={sx} onClick={onClick}>
        {value}
      </Typography>
    </Box>
  );
}

export default function LeaveApplicationInfo({
  leaveApplication,
  onClose,
  onEdit,
  setLeaveApplications,
  setSelectedLeaveApplication
}: LeaveApplicationInfoProps): JSX.Element {
  const dispatch = useDispatch();
  const isUserRcAdmin = useIsUserRcAdmin();

  const [loading, setLoading] = React.useState(false);
  const selectedLeaveApplication = leaveApplication;
  const [confirmReject, setConfirmReject] = React.useState(false);
  const [confirmDeletion, setConfirmDeletion] = React.useState(false);
  const [confirmApproval, setConfirmApproval] = React.useState(false);
  const [showEmployeeInfo, setShowEmployeeInfo] = React.useState(false);

  const activeUser = useSelector((state: RootState) => state.userContext.user);

  // handle delete leave application
  const handleDelete = async () => {
    try {
      const { success, message } = await deleteLeaveApplication(selectedLeaveApplication?.id || "");
      if (success) {
        dispatch(
          notificationAdd({
            id: new Date().getMilliseconds(),
            variant: "success",
            message,
            autoTimeout: true
          })
        );

        setLeaveApplications((prev) =>
          prev.filter((att) => att.id !== selectedLeaveApplication?.id)
        );
        setConfirmDeletion(false);
        onClose();
      }
    } catch (err: any) {
      dispatch(
        notificationAdd({
          id: new Date().getMilliseconds(),
          variant: "error",
          message: err?.message || "Error while deleting attendance",
          autoTimeout: true
        })
      );
    }
  };

  // handle approve leave application
  const handleApprove = async () => {
    if (!selectedLeaveApplication) return;

    setLoading(true);
    try {
      // Assuming there's an API to approve the leave application
      const { success, message, data } = await approveLeaveApplication({
        id: selectedLeaveApplication.id,
        decisionDate: new Date(),
        decisionBy: `${activeUser.firstName} ${activeUser.lastName}`
      });
      if (success) {
        dispatch(
          notificationAdd({
            id: new Date().getMilliseconds(),
            variant: "success",
            message,
            autoTimeout: true
          })
        );

        setLeaveApplications((prev) =>
          prev.map((application) =>
            application.id === selectedLeaveApplication.id ? data : application
          )
        );
        setSelectedLeaveApplication(data);
        setConfirmApproval(false);
      }
    } catch (err: any) {
      dispatch(
        notificationAdd({
          id: new Date().getMilliseconds(),
          variant: "error",
          message: err?.message || "Error while approving leave application",
          autoTimeout: true
        })
      );
    } finally {
      setLoading(false);
    }
  };

  // handle reject leave application
  const handleReject = async () => {
    if (!selectedLeaveApplication) return;
    setLoading(true);
    try {
      const { success, message, data } = await rejectLeaveApplication({
        id: selectedLeaveApplication.id,
        decisionDate: new Date(),
        decisionBy: `${activeUser.firstName} ${activeUser.lastName}`
      });

      if (success) {
        dispatch(
          notificationAdd({
            id: new Date().getMilliseconds(),
            variant: "success",
            message,
            autoTimeout: true
          })
        );

        setLeaveApplications((prev) =>
          prev.map((application) =>
            application.id === selectedLeaveApplication.id ? data : application
          )
        );
        setSelectedLeaveApplication(data);
        setConfirmReject(false);
      }
    } catch (err: any) {
      dispatch(
        notificationAdd({
          id: new Date().getMilliseconds(),
          variant: "error",
          message: err?.message || "Error while rejecting leave application",
          autoTimeout: true
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const titleJsx = (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
      }}
    >
      <Typography variant="h6" fontWeight={600}>
        {tl("leaveApplication.info")}
      </Typography>

      <Box display="flex" gap={1}>
        <IconButton size="small" onClick={() => onEdit(selectedLeaveApplication)}>
          <Edit fontSize="small" />
        </IconButton>
        <IconButton size="small" color="error" onClick={() => setConfirmDeletion(true)}>
          <Delete fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );

  return (
    <Panel
      onClose={() => onClose()}
      titleJsx={titleJsx}
      wrapperStyle={{
        maxWidth: "300px"
      }}
    >
      {confirmDeletion && (
        <OkhatiDialog
          title={tl("leaveApplication.delete")}
          description="Are you sure you want to delete this leave application?"
          next={handleDelete}
          cancel={() => setConfirmDeletion(false)}
          readMode={false}
        />
      )}

      {confirmApproval && (
        <OkhatiDialog
          title={tl("leaveApplication.approve")}
          description="Are you sure you want to approve this leave application?"
          next={handleApprove}
          cancel={() => setConfirmApproval(false)}
          readMode={false}
        />
      )}

      {confirmReject && (
        <OkhatiDialog
          title={tl("leaveApplication.reject")}
          description="Are you sure you want to reject this leave application?"
          next={handleReject}
          cancel={() => setConfirmReject(false)}
          readMode={false}
        />
      )}

      {showEmployeeInfo && (
        <EmployeeInfoPanel
          employee={
            selectedLeaveApplication?.employee || selectedLeaveApplication?.serviceProvider || null
          }
          onClose={() => setShowEmployeeInfo(false)}
        />
      )}

      <Box p={3} display="flex" flexDirection="column" gap={2}>
        <DetailRow
          label={t("name")}
          sx={{ cursor: "pointer", textDecoration: "underline", textTransform: "capitalize" }}
          onClick={() => setShowEmployeeInfo(true)}
          value={(() => {
            const emp =
              selectedLeaveApplication?.employee || selectedLeaveApplication?.serviceProvider;
            return emp ? `${emp.firstName} ${emp.lastName}` : "-";
          })()}
        />

        <DetailRow
          label={t("leaveApplication.startDate")}
          value={
            selectedLeaveApplication?.startDate
              ? bsFullDate(selectedLeaveApplication.startDate)
              : "-"
          }
        />

        <DetailRow
          label={t("leaveApplication.endDate")}
          value={
            selectedLeaveApplication?.endDate ? bsFullDate(selectedLeaveApplication.endDate) : "-"
          }
        />

        <DetailRow
          label={t("leaveApplication.daysCount")}
          value={selectedLeaveApplication?.daysCount || "-"}
        />

        <DetailRow
          label={t("leaveApplication.deductibleDaysCount")}
          value={selectedLeaveApplication?.deductibleDaysCount || "-"}
        />

        <Box display="flex" alignItems="center">
          <Typography variant="body2" fontWeight={600} width="150px">
            {t("leaveApplication.status")}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {selectedLeaveApplication?.status ? (
              <LeaveApplicationStatusChip status={selectedLeaveApplication.status} />
            ) : (
              "-"
            )}

            {selectedLeaveApplication?.status === LeaveApplicationStatus.PENDING &&
              isUserRcAdmin && (
                <Button
                  size="small"
                  variant="outlined"
                  loading={loading}
                  onClick={() => setConfirmApproval(true)}
                >
                  {tl("global.approve")}
                </Button>
              )}

            {selectedLeaveApplication?.status === LeaveApplicationStatus.PENDING &&
              isUserRcAdmin && (
                <Button
                  size="small"
                  variant="outlined"
                  loading={loading}
                  onClick={() => setConfirmReject(true)}
                  sx={{ backgroundColor: "error.main", color: "white" }}
                >
                  {tl("global.reject")}
                </Button>
              )}
          </Box>
        </Box>

        <DetailRow
          label={t("leaveApplication.reason")}
          value={selectedLeaveApplication?.reason || "-"}
        />

        {selectedLeaveApplication?.decisionBy && (
          <DetailRow
            label={t("leaveApplication.decisionBy")}
            value={selectedLeaveApplication.decisionBy}
          />
        )}

        {selectedLeaveApplication?.decisionDate && (
          <DetailRow
            label={t("leaveApplication.decisionDate")}
            value={bsFullDate(selectedLeaveApplication.decisionDate)}
          />
        )}
      </Box>
    </Panel>
  );
}
